import type { useAccountContext } from '@seek/hirer-graphql-react';
import { useTranslations } from '@vocab/react';
import {
  Actions,
  Alert,
  Button,
  Column,
  Columns,
  Heading,
  Stack,
  Text,
} from 'braid-design-system';
import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import translations from 'src/.vocab';
import lysTranslations from 'src/pages/LinkYourSoftware/.vocab';
import type { Partner, RelationshipTypeCode } from 'src/types';
import { useIsAuNz } from 'src/utils/hooks/useContext';

import type { FormValues } from '../../../ContentState';
import { ContactInfo } from '../ContactInfo/ContactInfo';

import { ContactName } from './components/ContactName/ContactName';
import { FutureApplications } from './components/FutureApplications/FutureApplications';
import { FuturePosting } from './components/FuturePosting/FuturePosting';
import { GoLiveDate } from './components/GoLiveDate/GoLiveDate';
import { HirerSelection } from './components/HirerSelection/HirerSelection';
import { SolutionSelection } from './components/SolutionSelection/SolutionSelection';
import { TermsAndConditions } from './components/TermsAndConditions/TermsAndConditions';

interface Props {
  partner: Partner;
  accountContext: ReturnType<typeof useAccountContext>['accountContext'];
}

export const Form = ({ partner, accountContext }: Props) => {
  const isAuNz = useIsAuNz();
  const { t: lys } = useTranslations(lysTranslations);
  const { t } = useTranslations(translations);

  const { solutions } = useWatch<FormValues>();
  const { setValue } = useFormContext<FormValues>();
  const selectedSolutionTypeCodes = solutions
    ? solutions.flatMap(({ typeCode, selected }) =>
        selected ? [typeCode as RelationshipTypeCode] : [],
      )
    : [];
  const selectedApplyWithSeek = selectedSolutionTypeCodes.includes(
    'APPLICATION_PREFILL',
  );
  const selectedPosting = selectedSolutionTypeCodes.includes('JOB_POSTING');
  const selectedApplicationExport =
    selectedSolutionTypeCodes.includes('APPLICATION_EXPORT');

  useEffect(() => {
    if (selectedApplicationExport || selectedApplyWithSeek) {
      setValue('futureApplications', undefined);
    }
  }, [setValue, selectedApplicationExport, selectedApplyWithSeek]);

  useEffect(() => {
    if (selectedPosting) {
      setValue('futurePosting', undefined);
    }
  }, [setValue, selectedPosting]);

  return (
    <Stack space="xlarge">
      <Heading level="2">{lys('connectionRequest')}</Heading>
      {accountContext?.currentUser?.status === 'Active' ? (
        <Columns space="xxlarge" collapseBelow="tablet">
          <Column>
            <Stack space="large">
              <HirerSelection />
              <ContactName />
              <GoLiveDate />
              <SolutionSelection partner={partner} />
              {selectedPosting &&
                !(selectedApplicationExport || selectedApplyWithSeek) && (
                  <FutureApplications partner={partner} />
                )}
              {!selectedPosting &&
                (selectedApplicationExport || selectedApplyWithSeek) && (
                  <FuturePosting />
                )}
              <TermsAndConditions />
              <Actions>
                <Button type="submit" tone="brandAccent">
                  {t('submit')}
                </Button>
              </Actions>
            </Stack>
          </Column>
          {isAuNz && (
            <Column width="2/5">
              <ContactInfo />
            </Column>
          )}
        </Columns>
      ) : (
        <Alert tone="critical" id="lys-account-not-active">
          <Text>{lys('accountNotActive')}</Text>
        </Alert>
      )}
    </Stack>
  );
};
